import {
  Control,
  Controller,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { Subheader } from '../../../tailwindComponents/Subheader';
import { Input } from '../../../tailwindComponents/Input';
import { Select } from '../../../tailwindComponents/Select';
import { Checkbox } from '../../../tailwindComponents/Checkbox';
import { Timepicker } from '../../../tailwindComponents/Timepicker';
import { DepotFormValues } from '../types';
import {
  CLOSING_TIME_FIELD_NAMES,
  DAYS,
  OPENING_TIME_FIELD_NAMES,
  STANDARD_BUSINESS_HOURS,
  WORKING_FIELD_NAMES,
} from '../constants';

function DayRow({
  keyName,
  booleanKeyName,
  openingTimeKeyName,
  closingTimeKeyName,
  label,
  control,
  formState,
  operating = false,
}: {
  keyName: string;
  booleanKeyName: string;
  openingTimeKeyName: string;
  closingTimeKeyName: string;
  label: string;
  control: Control<DepotFormValues>;
  formState: UseFormStateReturn<DepotFormValues>;
  operating?: boolean;
}) {
  type WorkingFieldName = (typeof WORKING_FIELD_NAMES)[number];
  type OpeningTimeFieldName = (typeof OPENING_TIME_FIELD_NAMES)[number];
  type ClosingTimeFieldName = (typeof CLOSING_TIME_FIELD_NAMES)[number];

  const working = useWatch({
    control,
    name: `${booleanKeyName}` as WorkingFieldName,
  });

  return (
    <div key={keyName} className='flex items-center gap-x-6'>
      <Controller
        name={booleanKeyName as WorkingFieldName}
        control={control}
        render={({ field }) => (
          <div className='min-w-[12rem] h-8 flex items-center'>
            <Checkbox
              label={`${
                operating ? `Operating on ${label}` : `Working on ${label}`
              }`}
              checked={!!field.value}
              onChange={field.onChange}
              name={field.name}
              ref={field.ref}
            />
          </div>
        )}
      />
      <Controller
        name={openingTimeKeyName as OpeningTimeFieldName}
        control={control}
        render={({ field }) => (
          <Timepicker
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!(formState.errors as any)?.[keyName]?.openingTime}
            optional={!working}
            orientation='horizontal'
            disabled={!working}
          />
        )}
      />
      <span className='text-gray-400 text-lg select-none'>–</span>
      <Controller
        name={closingTimeKeyName as ClosingTimeFieldName}
        control={control}
        render={({ field }) => (
          <Timepicker
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!(formState.errors as any)?.[keyName]?.closingTime}
            optional={!working}
            orientation='horizontal'
            disabled={!working}
          />
        )}
      />
    </div>
  );
}

export function FleetAndHours({
  control,
  formState,
}: {
  control: Control<DepotFormValues>;
  formState: UseFormStateReturn<DepotFormValues>;
}) {
  const haveOwnFleet = useWatch({
    control,
    name: 'haveOwnFleet',
  });

  const schedule = useWatch({
    control,
    name: 'schedule',
  });

  return (
    <>
      <Subheader subHeader='Transport Fleet' />
      <Controller
        name='haveOwnFleet'
        control={control}
        render={({ field }) => (
          <Checkbox
            label='Has own fleet'
            checked={!!field.value}
            onChange={field.onChange}
            name={field.name}
            ref={field.ref}
          />
        )}
      />
      {haveOwnFleet && (
        <div className='grid grid-cols-2 gap-x-8 gap-y-4 max-w-3xl'>
          <div className='flex flex-col gap-y-4'>
            <Controller
              name='articsOver26t'
              control={control}
              render={({ field }) => (
                <Input
                  label='Artics over 26t'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.articsOver26t}
                  validationMessage={formState.errors.articsOver26t?.message}
                />
              )}
            />
            <Controller
              name='rigidsUnder26t'
              control={control}
              render={({ field }) => (
                <Input
                  label='Rigids under 26t'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.rigidsUnder26t}
                  validationMessage={formState.errors.rigidsUnder26t?.message}
                />
              )}
            />
            <Controller
              name='rigidsUnder18t'
              control={control}
              render={({ field }) => (
                <Input
                  label='Rigids under 18t'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.rigidsUnder18t}
                  validationMessage={formState.errors.rigidsUnder18t?.message}
                />
              )}
            />
            <Controller
              name='rigidsUnder7_5t'
              control={control}
              render={({ field }) => (
                <Input
                  label='Rigids under 7.5t'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.rigidsUnder7_5t}
                  validationMessage={formState.errors.rigidsUnder7_5t?.message}
                />
              )}
            />
          </div>
          <div className='flex flex-col gap-y-4'>
            <Controller
              name='ldvsUnder3_5t'
              control={control}
              render={({ field }) => (
                <Input
                  label='LDVs under 3.5t'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.ldvsUnder3_5t}
                  validationMessage={formState.errors.ldvsUnder3_5t?.message}
                />
              )}
            />
            <Controller
              name='vansUnder1_5t'
              control={control}
              render={({ field }) => (
                <Input
                  label='Vans under 1.5t'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.vansUnder1_5t}
                  validationMessage={formState.errors.vansUnder1_5t?.message}
                />
              )}
            />
            <Controller
              name='specialOther'
              control={control}
              render={({ field }) => (
                <Input
                  label='Special / Other'
                  {...field}
                  optional
                  orientation='horizontal'
                  isInvalid={!!formState.errors.specialOther}
                  validationMessage={formState.errors.specialOther?.message}
                />
              )}
            />
          </div>
        </div>
      )}

      <Subheader subHeader='Working Hours' />
      <div className='max-w-md'>
        <Controller
          name='schedule'
          control={control}
          render={({ field }) => (
            <Select
              label='Schedule'
              options={STANDARD_BUSINESS_HOURS}
              selected={field.value}
              setSelected={field.onChange}
              orientation='horizontal'
            />
          )}
        />
      </div>
      {schedule.value === STANDARD_BUSINESS_HOURS[0].value ? (
        <Controller
          name='flexibleHoursOnDemand'
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Flexible hours on demand'
              checked={field.value}
              onChange={field.onChange}
              name={field.name}
              ref={field.ref}
            />
          )}
        />
      ) : (
        <div className='flex flex-col space-y-4'>
          {DAYS.map(({ key, label }) => (
            <DayRow
              key={key}
              keyName={`${key}WorkingHours`}
              booleanKeyName={`${key}WorkingHours.working`}
              openingTimeKeyName={`${key}WorkingHours.openingTime`}
              closingTimeKeyName={`${key}WorkingHours.closingTime`}
              label={label}
              control={control}
              formState={formState}
            />
          ))}
        </div>
      )}
      <Subheader subHeader='Loading Hours' />
      <div className='flex flex-col space-y-4'>
        {DAYS.map(({ key, label }) => (
          <DayRow
            key={key}
            keyName={`${key}LoadingHours`}
            booleanKeyName={`${key}LoadingHours.operating`}
            openingTimeKeyName={`${key}LoadingHours.openingTime`}
            closingTimeKeyName={`${key}LoadingHours.closingTime`}
            label={label}
            control={control}
            formState={formState}
            operating
          />
        ))}
      </div>
    </>
  );
}
